import React from 'react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function PrivacyPolicyPage() {
  const lastUpdated = 'April 2, 2026';
  return <div className="bg-viridia-black min-h-screen pt-32 pb-24" data-id="element-1722">
      <SEOHead title="Privacy Policy | Viridia Analytics" description="Privacy Policy for Viridia Analytics. Learn how we collect, use, and protect your personal data." canonical="/privacy" data-id="element-1723" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1724">
        <ScrollReveal data-id="element-1725">
          <div className="text-center mb-16" data-id="element-1726">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" data-id="element-1727">
              Privacy Policy
            </h1>
            <p className="text-gray-400" data-id="element-1728">Last Updated: {lastUpdated}</p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1} data-id="element-1729">
          <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-12 prose prose-invert max-w-none" data-id="element-1730">
            <div className="space-y-8 text-gray-300 leading-relaxed" data-id="element-1731">
              <section data-id="element-1732">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1733">
                  1. Introduction
                </h2>
                <p data-id="element-1734">
                  Viridia Analytics ("we," "our," or "us") respects your privacy
                  and is committed to protecting your personal data. This
                  Privacy Policy explains how we collect, use, disclose, and
                  safeguard your information when you visit our website or use
                  our marketing and automation services.
                </p>
              </section>

              <section data-id="element-1735">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1736">
                  2. Information We Collect
                </h2>
                <p className="mb-2" data-id="element-1737">
                  We may collect the following types of information:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-1738">
                  <li data-id="element-1739">
                    <strong data-id="element-1740">Personal Identification Information:</strong> Name,
                    email address, phone number (including for SMS
                    communications), and company details when you fill out a
                    contact form, book a consultation, or opt in to messaging.
                  </li>
                  <li data-id="element-1741">
                    <strong data-id="element-1742">Business Information:</strong> Information about
                    your business, marketing goals, current advertising
                    platforms, and CRM systems.
                  </li>
                  <li data-id="element-1743">
                    <strong data-id="element-1744">Usage Data:</strong> Information about how you
                    interact with our website, including IP addresses, browser
                    types, pages visited, and time spent on pages.
                  </li>
                </ul>
              </section>

              <section data-id="element-1745">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1746">
                  3. How We Use Your Information
                </h2>
                <p className="mb-2" data-id="element-1747">
                  We use the collected information for various purposes,
                  including:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-1748">
                  <li data-id="element-1749">To provide, operate, and maintain our services.</li>
                  <li data-id="element-1750">
                    To communicate with you, including responding to inquiries
                    and providing customer support.
                  </li>
                  <li data-id="element-1751">
                    To send administrative information, such as updates to our
                    terms or policies.
                  </li>
                  <li data-id="element-1752">
                    To analyze usage patterns and improve our website and
                    service offerings.
                  </li>
                  <li data-id="element-1753">
                    To manage and optimize advertising campaigns on your behalf.
                  </li>
                </ul>
              </section>

              <section data-id="element-1754">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1755">
                  4. SMS/Text Messaging Privacy
                </h2>
                <p className="mb-2" data-id="element-1756">
                  If you opt in to receive text messages from us, the following
                  applies:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-1757">
                  <li data-id="element-1758">
                    We collect phone numbers when users submit contact forms,
                    book consultations, or opt in to receive text messages.
                  </li>
                  <li data-id="element-1759">
                    Phone numbers may be used to send appointment confirmations,
                    follow-up messages, service updates, and promotional offers
                    via SMS.
                  </li>
                  <li data-id="element-1760">
                    By providing your phone number and opting in, you consent to
                    receive automated text messages from Viridia Analytics.
                  </li>
                  <li data-id="element-1761">
                    Message and data rates may apply. Message frequency varies.
                  </li>
                  <li data-id="element-1762">
                    You can opt out at any time by replying{' '}
                    <strong data-id="element-1763">STOP</strong> to any message.
                  </li>
                  <li data-id="element-1764">
                    Reply <strong data-id="element-1765">HELP</strong> for assistance.
                  </li>
                  <li data-id="element-1766">
                    <strong data-id="element-1767">
                      SMS consent and phone numbers are NOT shared with third
                      parties or affiliates for marketing purposes.
                    </strong>
                  </li>
                  <li data-id="element-1768">
                    We use third-party platforms (e.g.,
                    GoHighLevel/LeadConnector) solely to facilitate our own SMS
                    communications with you.
                  </li>
                </ul>
              </section>

              <section data-id="element-1769">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1770">
                  5. Cookies and Tracking Technologies
                </h2>
                <p data-id="element-1771">
                  We use cookies and similar tracking technologies to track
                  activity on our website and hold certain information. Cookies
                  are files with a small amount of data which may include an
                  anonymous unique identifier. You can instruct your browser to
                  refuse all cookies or to indicate when a cookie is being sent.
                </p>
              </section>

              <section data-id="element-1772">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1773">
                  6. Third-Party Services
                </h2>
                <p data-id="element-1774">
                  In providing our services, we interact with third-party
                  platforms such as Google Ads, Meta (Facebook/Instagram), Yelp,
                  and various CRM providers. We may share necessary information
                  with these platforms solely for the purpose of executing the
                  marketing services you have contracted us to perform. We do
                  not sell your personal data to third parties.
                </p>
              </section>

              <section data-id="element-1775">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1776">
                  7. Data Security
                </h2>
                <p data-id="element-1777">
                  We implement appropriate technical and organizational security
                  measures designed to protect the security of any personal
                  information we process. However, please note that no
                  electronic transmission over the Internet or information
                  storage technology can be guaranteed to be 100% secure.
                </p>
              </section>

              <section data-id="element-1778">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1779">
                  8. Your Rights
                </h2>
                <p data-id="element-1780">
                  Depending on your location, you may have certain rights
                  regarding your personal data, including the right to access,
                  correct, or delete the information we hold about you. To
                  exercise these rights, please contact us using the information
                  provided below.
                </p>
              </section>

              <section data-id="element-1781">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1782">
                  9. Changes to This Privacy Policy
                </h2>
                <p data-id="element-1783">
                  We may update our Privacy Policy from time to time. We will
                  notify you of any changes by posting the new Privacy Policy on
                  this page and updating the "Last Updated" date.
                </p>
              </section>

              <section data-id="element-1784">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1785">
                  10. Contact Us
                </h2>
                <p className="mb-2" data-id="element-1786">
                  If you have any questions about this Privacy Policy, please
                  contact us:
                </p>
                <ul className="list-none space-y-1" data-id="element-1787">
                  <li data-id="element-1788">
                    <strong data-id="element-1789">Company:</strong> Viridia Analytics
                  </li>
                  <li data-id="element-1790">
                    <strong data-id="element-1791">Location:</strong> Phoenix, AZ
                  </li>
                  <li data-id="element-1792">
                    <strong data-id="element-1793">Email:</strong>{' '}
                    <a href="mailto:info@viridiaanalytics.com" className="text-viridia hover:underline" data-id="element-1794">
                      info@viridiaanalytics.com
                    </a>
                  </li>
                  <li data-id="element-1795">
                    <strong data-id="element-1796">Phone:</strong>{' '}
                    <a href="tel:6233000708" className="text-viridia hover:underline" data-id="element-1797">
                      (623) 300-0708
                    </a>
                  </li>
                </ul>
              </section>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </div>;
}